/**
 * Declarative fuel-supplier import profiles.
 *
 * Adding a new fuel company is autonomous: a company added in the `fuel_companies`
 * DB table automatically gets a working preset via `buildGenericProfile` (target
 * table, constant fuel_company_id resolved from the DB row, generic alias
 * auto-mapping). Only suppliers whose file layout is unusual need an explicit
 * entry in `STATIC_FUEL_SUPPLIER_PROFILES` — that is one declarative object, not
 * a new code path.
 */
import i18n from '@/i18n/config';
import {
  CIRCLE_K_PURCHASE_DATE_ALIASES,
  CIRCLE_K_PURCHASE_TIME_ALIASES,
  CIRCLE_K_CUSTOMER_ID_ALIASES,
  OKQ8_PURCHASE_DATE_ALIASES,
  OKQ8_PURCHASE_TIME_ALIASES,
  OKQ8_CUSTOMER_ID_ALIASES,
  OKQ8_CARD_ACCOUNT_ALIASES,
  OKQ8_CARD_SERIAL_ALIASES,
  PREEM_PURCHASE_DATE_ALIASES,
  PREEM_PURCHASE_TIME_ALIASES,
  PREEM_CUSTOMER_ID_ALIASES,
  ST1_PURCHASE_DATE_ALIASES,
  ST1_PURCHASE_TIME_ALIASES,
  QSTAR_PURCHASE_DATE_ALIASES,
  QSTAR_PURCHASE_TIME_ALIASES,
  DINX_PURCHASE_DATE_ALIASES,
  DINX_PURCHASE_TIME_ALIASES,
  FUEL_SUPPLIER_DISALLOWED_TARGETS,
} from './types';

export const FUEL_TARGET_TABLE = 'fuel_transactions';

export interface FuelCompanyRef {
  id: number;
  name: string;
}

export interface FuelSupplierPurchasedConfig {
  dateAliases: string[];
  timeAliases: string[];
  /** sourceFormat for the date column: 'YYYYMMDD' | 'YYYY-MM-DD' | 'EXCEL_SERIAL' | … */
  dateFormat: string;
  /** sourceFormat for the time column when combined: 'HHmm' | 'HH:mm:ss' | … */
  timeFormat: string;
  /** Circle K parity: rebuild an already-present `purchased` mapping if alias-build fails. */
  reconstructFallback: boolean;
}

/** Compose card_number from account + zero-padded serial (OKQ8). */
export interface FuelSupplierCardNumberConfig {
  accountAliases: string[];
  serialAliases: string[];
  /** Pad Card Serial Number to this many characters with leading zeros. */
  serialPadLength: number;
}

export interface FuelSupplierProfile {
  /** Stable preset key; also used as the `data-testid` suffix (`preset-<key>`). */
  key: string;
  /** Fallback button label (the live DB company name is preferred when available). */
  label: string;
  /** Lowercased tokens; if any appears in the supplier fingerprint the profile is active. */
  matchTokens: string[];
  /** `fuel_companies.name` value(s) used to resolve `fuel_company_id` from the DB. */
  companyNames: string[];
  /** Used only if DB resolution fails, so legacy hardcoded ids still apply. */
  fallbackFuelCompanyId: number | null;
  file: {
    delimiter: string;
    decimalSeparator: ',' | '.';
    encoding: string;
    /** null = do not change the current format selection. */
    formats: string[] | null;
    hasHeader: boolean;
    /** Leading non-table rows to skip (e.g. invoice header block). Default 0. */
    skipRows?: number;
    /** Celler ovanför tabellen som injiceras på varje datarad (A1-adress). */
    headerCells?: Array<{
      cell: string;
      asColumn: string;
      targetField?: string;
      split?: {
        pattern: string;
        parts: Array<{ group: number; asColumn: string; targetField?: string }>;
      };
    }>;
  };
  /** null = no supplier-specific datetime handling (generic auto-map handles it). */
  purchased: FuelSupplierPurchasedConfig | null;
  /** Optional composed card_number (e.g. OKQ8 Account + padded Serial). */
  cardNumber?: FuelSupplierCardNumberConfig;
  /** Formats accepted on the saved definition; undefined = derive from selection/preview. */
  acceptFormats?: string[];
  customerIdAliases: string[];
  /** Targets never present in supplier files (resolved later via card mapping). */
  disallowedTargets: string[];
  description: string;
  /** True for profiles synthesised from a DB company without an explicit entry. */
  isGeneric?: boolean;
}

const DISALLOWED = Array.from(FUEL_SUPPLIER_DISALLOWED_TARGETS);

/**
 * Explicit profiles for suppliers with a known, non-generic file layout.
 * Order matters only for preset-button ordering; matching is token-based.
 */
export const STATIC_FUEL_SUPPLIER_PROFILES: FuelSupplierProfile[] = [
  {
    key: 'circle-k',
    label: 'Circle K',
    matchTokens: ['circle k', 'circlek'],
    companyNames: ['Circle K'],
    fallbackFuelCompanyId: 2,
    file: { delimiter: ';', decimalSeparator: ',', encoding: 'ISO-8859-1', formats: null, hasHeader: true },
    purchased: {
      dateAliases: CIRCLE_K_PURCHASE_DATE_ALIASES,
      timeAliases: CIRCLE_K_PURCHASE_TIME_ALIASES,
      dateFormat: 'YYYYMMDD',
      timeFormat: 'HHmm',
      reconstructFallback: true,
    },
    acceptFormats: ['csv', 'dat'],
    customerIdAliases: CIRCLE_K_CUSTOMER_ID_ALIASES,
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportFromCompany', { company: 'Circle K' });
    },
  },
  {
    key: 'preem',
    label: 'Preem',
    matchTokens: ['preem'],
    companyNames: ['Preem'],
    fallbackFuelCompanyId: 3,
    // Preem exports use an international/English layout: UTF-8, dot decimals, ISO dates.
    file: { delimiter: ';', decimalSeparator: '.', encoding: 'UTF-8', formats: ['csv'], hasHeader: true },
    purchased: {
      dateAliases: PREEM_PURCHASE_DATE_ALIASES,
      timeAliases: PREEM_PURCHASE_TIME_ALIASES,
      dateFormat: 'YYYY-MM-DD',
      timeFormat: 'HH:mm:ss',
      reconstructFallback: false,
    },
    customerIdAliases: PREEM_CUSTOMER_ID_ALIASES,
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportFromCompany', { company: 'Preem' });
    },
  },
  {
    key: 'okq8',
    label: 'OKQ8',
    matchTokens: ['okq8', 'ok q8'],
    companyNames: ['OKQ8'],
    fallbackFuelCompanyId: 1,
    // EN-rapporter (K02/K05) använder punkt-decimal + komma-tusen; SV-export oftast punkt.
    // Runtime sätter numberFormat per filnamnsvariant (K02/K05) i importExecutor.
    file: { delimiter: ';', decimalSeparator: '.', encoding: 'UTF-8', formats: ['xlsx', 'xls'], hasHeader: true },
    purchased: {
      dateAliases: OKQ8_PURCHASE_DATE_ALIASES,
      timeAliases: OKQ8_PURCHASE_TIME_ALIASES,
      // EN K02/K05: DD-MM-YYYY + HH:mm:ss. SV Excel-serial faller bakåt via parseDateParts.
      dateFormat: 'DD-MM-YYYY',
      timeFormat: 'HH:mm:ss',
      reconstructFallback: true,
    },
    cardNumber: {
      accountAliases: OKQ8_CARD_ACCOUNT_ALIASES,
      serialAliases: OKQ8_CARD_SERIAL_ALIASES,
      serialPadLength: 4,
    },
    acceptFormats: ['xlsx', 'xls'],
    customerIdAliases: OKQ8_CUSTOMER_ID_ALIASES,
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportOkq8Description');
    },
  },
  {
    key: 'st1',
    label: 'St1',
    matchTokens: ['st1'],
    companyNames: ['St1'],
    fallbackFuelCompanyId: 5,
    // St1 delivers .xlsx with Swedish headers and Swedish decimal commas.
    file: { delimiter: ';', decimalSeparator: ',', encoding: 'UTF-8', formats: ['xlsx', 'xls'], hasHeader: true },
    purchased: {
      // "Tidpunkt" is one column holding "YYYY-MM-DD HH:mm" (no separate time column).
      dateAliases: ST1_PURCHASE_DATE_ALIASES,
      timeAliases: ST1_PURCHASE_TIME_ALIASES,
      dateFormat: 'YYYY-MM-DD HH:mm',
      timeFormat: 'HH:mm',
      reconstructFallback: false,
    },
    acceptFormats: ['xlsx', 'xls'],
    // "Kundnummer" → customer_number (customer_id is disallowed for fuel imports).
    customerIdAliases: ['kundnummer', 'kundnr'],
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportFromCompany', { company: 'St1' });
    },
  },
  {
    key: 'qstar',
    label: 'Qstar/Bilisten',
    matchTokens: ['qstar', 'q-star', 'q star', 'bilisten', 'fordonsspecifikation'],
    companyNames: ['Qstar/Bilisten/Pump', 'Qstar', 'Bilisten'],
    fallbackFuelCompanyId: 9,
    // "Fordonsspecifikation" .xlsx: rows 1-6 are an invoice header block, the real
    // table header is on row 7 (skipRows: 6). B3/B4/B5 apply to every data row.
    file: {
      delimiter: ';',
      decimalSeparator: ',',
      encoding: 'UTF-8',
      formats: ['xlsx', 'xls'],
      hasHeader: true,
      skipRows: 6,
      headerCells: [
        {
          cell: 'B3',
          asColumn: 'Header Kund',
          split: {
            // Namn är valfritt — vissa filer har bara "104216" i B3.
            pattern: '^(\\d+)(?:\\s+(.+))?$',
            parts: [
              { group: 1, asColumn: 'Header Kundnr', targetField: 'customer_number' },
              { group: 2, asColumn: 'Header Kundnamn', targetField: 'customer_name' },
            ],
          },
        },
        { cell: 'B4', asColumn: 'Header Fakturanr', targetField: 'invoice_number' },
        { cell: 'B5', asColumn: 'Header Fakturadatum', targetField: 'invoice_date' },
      ],
    },
    purchased: {
      dateAliases: QSTAR_PURCHASE_DATE_ALIASES,
      timeAliases: QSTAR_PURCHASE_TIME_ALIASES,
      dateFormat: 'YYYY-MM-DD HH:mm',
      timeFormat: 'HH:mm',
      reconstructFallback: false,
    },
    acceptFormats: ['xlsx', 'xls'],
    customerIdAliases: [],
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportFromCompany', { company: 'Qstar/Bilisten' });
    },
  },
  {
    key: 'din-x',
    label: 'Din-X',
    matchTokens: ['din-x', 'dinx', 'din x'],
    companyNames: ['Din-X', 'DinX'],
    fallbackFuelCompanyId: 10,
    // Din-X terminal .csv (;). "Kort" = account name, "Data" = card id, zero-volume
    // rows filtered out. Backend self-heals via DINX_DEFAULT_MAPPINGS.
    file: { delimiter: ';', decimalSeparator: ',', encoding: 'UTF-8', formats: ['csv'], hasHeader: true },
    purchased: {
      dateAliases: DINX_PURCHASE_DATE_ALIASES,
      timeAliases: DINX_PURCHASE_TIME_ALIASES,
      dateFormat: 'DD-MM-YYYY HH:mm:ss',
      timeFormat: 'HH:mm:ss',
      reconstructFallback: false,
    },
    acceptFormats: ['csv'],
    customerIdAliases: [],
    disallowedTargets: DISALLOWED,
    get description() {
      return i18n.t('copy.admin.fuelImportFromCompany', { company: 'Din-X' });
    },
  },
];

const SWEDISH_SLUG_MAP: Record<string, string> = { å: 'a', ä: 'a', ö: 'o', é: 'e', ü: 'u', ø: 'o', æ: 'ae' };

export const slugifyCompanyName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[åäöéüøæ]/g, (ch) => SWEDISH_SLUG_MAP[ch] ?? ch)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

/** Profile for any DB company without an explicit entry — works with zero code changes. */
export const buildGenericProfile = (company: FuelCompanyRef): FuelSupplierProfile => ({
  key: `company-${slugifyCompanyName(company.name) || company.id}`,
  label: company.name,
  matchTokens: [company.name.toLowerCase()],
  companyNames: [company.name],
  fallbackFuelCompanyId: company.id,
  file: { delimiter: ';', decimalSeparator: ',', encoding: 'UTF-8', formats: null, hasHeader: true },
  purchased: null,
  customerIdAliases: [],
  disallowedTargets: DISALLOWED,
  description: i18n.t('copy.admin.fuelImportFromCompany', { company: company.name }),
  isGeneric: true,
});

const nameCovered = (name: string, profiles: FuelSupplierProfile[]): boolean =>
  profiles.some((p) => p.companyNames.some((cn) => cn.toLowerCase() === name.toLowerCase()));

/**
 * Full ordered profile list: explicit profiles first (stable preset order/test ids),
 * then a generic profile per active DB company not already covered.
 */
export const buildProfileList = (companies: FuelCompanyRef[]): FuelSupplierProfile[] => {
  const generic = companies
    .filter((c) => !nameCovered(c.name, STATIC_FUEL_SUPPLIER_PROFILES))
    .map(buildGenericProfile);
  return [...STATIC_FUEL_SUPPLIER_PROFILES, ...generic];
};

/** Resolve the active profile from the fingerprint (only for the fuel target table). */
export const resolveActiveProfile = (
  fingerprint: string,
  selectedTable: string,
  companies: FuelCompanyRef[],
): FuelSupplierProfile | null => {
  if (selectedTable !== FUEL_TARGET_TABLE) return null;
  const fp = fingerprint.toLowerCase();
  for (const profile of buildProfileList(companies)) {
    if (profile.matchTokens.some((tok) => tok && fp.includes(tok))) return profile;
  }
  return null;
};

/** DB-driven fuel_company_id (env-correct); falls back to the legacy hardcoded id. */
export const resolveFuelCompanyId = (
  profile: FuelSupplierProfile,
  companies: FuelCompanyRef[],
): number | null => {
  for (const cn of profile.companyNames) {
    const match = companies.find((c) => c.name.toLowerCase() === cn.toLowerCase());
    if (match) return match.id;
  }
  return profile.fallbackFuelCompanyId;
};

/** Find a profile by preset key (explicit or generic from the DB list). */
export const findProfileByKey = (
  key: string,
  companies: FuelCompanyRef[],
): FuelSupplierProfile | null => buildProfileList(companies).find((p) => p.key === key) ?? null;
